import React, { useState, useRef, useEffect, forwardRef } from 'react';
import { getLabelsByCategoryAndSection } from '../../api/api.js';
import { enterPtsEdit, exitPtsEdit } from "../utils/ptsEditMode.js";
import { usePTS } from '../../context/PTSContext.js';
import { exportTutkimustarpeetToExcel } from '../utils/exportTutkimustarpeetToExcel.js';

const TutkimustarpeetTaulu = forwardRef(({ showAlvZero = false, hide11thYear = false }, ref) => {
  const {
    years: allYears,
    currentYear,
    tutkimusData,
    setTutkimusData,
    tutkimusYhteensa,
    handleSavePTS,
    removeTableRow
  } = usePTS();

  // Display-only truncation — the underlying 11-value arrays are untouched,
  // same pattern as Tekniikkataulut.js/PTSLongTermTable.js.
  const years = hide11thYear ? allYears.slice(0, 10) : allYears;

  const initialData = [
    { header: 'Tutkimustarpeet', items: [{ label: '', values: Array(11).fill('') }] },
  ];

  const [tableData, setTableData] = useState(
    tutkimusData.length ? tutkimusData : initialData
  );
  const [originalData, setOriginalData] = useState(tableData);
  const [isEditing, setIsEditing] = useState(false);
  const registeredRef = useRef(false);

  /* ---------------- EDIT MODE SYNC ---------------- */

  useEffect(() => {
    if (isEditing && !registeredRef.current) {
      enterPtsEdit();
      registeredRef.current = true;
    }
    if (!isEditing && registeredRef.current) {
      exitPtsEdit();
      registeredRef.current = false;
    }
    return () => exitPtsEdit();
  }, [isEditing]);

  /* ---------------- CONTEXT SYNC ---------------- */

  useEffect(() => {
    if (tutkimusData.length) setTableData(tutkimusData);
  }, [tutkimusData]);

  const syncToContext = (updated) => {
    setTutkimusData(updated);
  };

  /* ---------------- HANDLERS ---------------- */

  const handleAddRow = () => {
    const updated = structuredClone(tableData);
    updated[0].items.push({ label: '', values: Array(11).fill('') });
    setTableData(updated);
    syncToContext(updated);
  };

const handleRemoveRow = (itemIdx) => {
  removeTableRow("Lisätutkimukset", 0, itemIdx);
};

  const handleValueChange = (itemIdx, yearIdx, value) => {
    const updated = structuredClone(tableData);
    updated[0].items[itemIdx].values[yearIdx] = value;
    setTableData(updated);
    syncToContext(updated);
  };

  const handleLabelChange = (itemIdx, value) => {
    const updated = structuredClone(tableData);
    updated[0].items[itemIdx].label = value;
    setTableData(updated);
    syncToContext(updated);
  };

  /* ---------------- LABEL FETCH ---------------- */

  const [labels, setLabels] = useState([]);

  useEffect(() => {
    if (!isEditing) return;
    getLabelsByCategoryAndSection('Lisätutkimukset', 'Tutkimustarpeet')
      .then(res => setLabels(res.data.labels || []))
      .catch(console.error);
  }, [isEditing]);

  /* ---------------- SAVE / CANCEL ---------------- */

  const handleEditToggle = () => {
    if (!isEditing) {
      setOriginalData(structuredClone(tableData));
      setIsEditing(true);
    } else {
      handleSavePTS("Lisätutkimukset");
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    const restored = structuredClone(originalData);
    setTableData(restored);
    syncToContext(restored);
    setIsEditing(false);
  };

  /* ---------------- RENDER ---------------- */

  return (
    <div className="my-4 ptstaulut">
        <div className="text-center mb-2">
          {isEditing ? (
            <>
              <button className="btn btn-sm btn-success me-2" onClick={handleEditToggle}>Tallenna</button>
              <button className="btn btn-sm btn-secondary" onClick={handleCancel}>Peruuta</button>
            </>
          ) : (
            <>
              <button className="btn btn-sm btn-success me-2" onClick={handleEditToggle}>Muokkaa</button>
              <button
                className="btn btn-sm btn-outline-success"
                onClick={() =>
                  exportTutkimustarpeetToExcel({
                    tableData,
                    totals: tutkimusYhteensa,
                    years
                  })
                }
              >
                Lataa Excel
              </button>
            </>
          )}
        </div>

      <div className="table-responsive pts-react-to-edit" ref={ref}>
        <table className="table table-sm table-borderless table-striped mb-0">
          <thead>
            <tr>
              <th colSpan={years.length + 1} className="bg-success text-white p-2">
                {!isEditing && (
                  <div className="small text-end">
                    {showAlvZero
                      ? `Kustannusarvio (x 1000€) Kustannustaso ${currentYear} ALV 0%`
                      : `Kustannusarvio (x 1000€) Kustannustaso ${currentYear} sis. Alv 25,5%`}
                  </div>
                )}
              </th>
            </tr>
            <tr>
              <th className="bg-success text-white text-start">Tutkimustarpeet</th>
              {years.map(y => (
                <th key={y} className="bg-success text-white text-center fw-bold">{y}</th>
              ))}
            </tr>
          </thead>

          <tbody>
            {tableData[0].items.map((item, idx) => (
              <tr key={idx}>
                <td>
                  {isEditing ? (
                    <>
                      <input
                        type="text"
                        list={`labels-${idx}`}
                        value={item.label}
                        onChange={e => handleLabelChange(idx, e.target.value)}
                        className="form-control form-control-sm"
                      />
                      <datalist id={`labels-${idx}`}>
                        {labels.map((l, i) => <option key={i} value={l} />)}
                      </datalist>
                    </>
                  ) : item.label}
                </td>

                {item.values.slice(0, years.length).map((val, yIdx) => (
                  <td key={yIdx} className="text-center px-1">
                    {isEditing ? (
                      <input
                        className={`form-control form-control-sm text-center ${
                          val && Number(val) > 0 ? 'bg-success text-white' : ''
                        }`}
                        value={val || ''}
                        onChange={e => handleValueChange(idx, yIdx, e.target.value)}
                      />
                    ) : (!val || Number(val) === 0 ? '' : val)}
                  </td>
                ))}

                {isEditing && (
                  <td>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleRemoveRow(idx)}
                    >
                      ×
                    </button>
                  </td>
                )}
              </tr>
            ))}

            {isEditing && (
              <tr>
                <td colSpan={years.length + 1}>
                  <button className="btn btn-sm btn-success" onClick={handleAddRow}>
                    Lisää rivi
                  </button>
                </td>
              </tr>
            )}
          </tbody>

          <tfoot>
            <tr className="fw-bold">
              <td className="bg-success text-white">YHTEENSÄ</td>
              {tutkimusYhteensa.slice(0, years.length).map((sum, i) => (
                <td key={i} className="bg-success text-white text-center font-monospace">
                  {sum === 0 ? '' : sum}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
});

export default TutkimustarpeetTaulu;
